/**
 * Operating Environment — general/physical env, usage scenarios,
 * network threat environment. Pulls from
 * `formatted.operating_environment`. Always renders.
 */
import React from 'react';
import { SectionCard, SubSection, Field, StringListField, ObjectListField, type SectionProps } from './parts';

const PATH = ['operating_environment'];

const joinList = (v) => (Array.isArray(v) ? v.join(', ') : v);

export default function OperatingEnvironment({ data, isEditing, onChange }: SectionProps) {
  const oe = data ?? {};
  const phys = oe.physical_environment ?? {};
  const scenarios = oe.usage_scenarios?.scenarios ?? [];
  const threatEnvs = oe.network_threat_environment?.assessments ?? [];

  const scenarioColumns = [
    { key: 'scenario_name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'user_types', label: 'User Types', render: joinList },
    { key: 'security_implications', label: 'Security Implications', render: joinList },
    { key: 'applicable_threats', label: 'Applicable Threats', render: joinList },
  ];

  const threatColumns = [
    { key: 'connection_type', label: 'Connection Type' },
    { key: 'threat_level', label: 'Threat Level' },
    { key: 'threat_actors', label: 'Threat Actors', render: joinList },
    { key: 'common_attack_vectors', label: 'Attack Vectors', render: joinList },
    { key: 'mitigation_measures', label: 'Mitigations', render: joinList },
  ];

  return (
    <SectionCard title="Operating Environment" id="operating_environment">
      <SubSection title="General environment">
        <Field label="Description" value={oe.general_environment} isEditing={isEditing} onChange={onChange} path={[...PATH, 'general_environment']} multiline />
      </SubSection>

      <SubSection title="Physical environment">
        <Field label="Deployment location" value={phys.deployment_location} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_environment', 'deployment_location']} />
        <Field label="Temperature range" value={phys.temperature_range} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_environment', 'temperature_range']} />
        <Field label="Humidity range" value={phys.humidity_range} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_environment', 'humidity_range']} />
        <Field label="Vibration level" value={phys.vibration_level} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_environment', 'vibration_level']} />
        <Field label="IP rating" value={phys.ip_rating} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_environment', 'ip_rating']} />
        <Field label="Physical access required" value={phys.physical_access_required} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_environment', 'physical_access_required']} multiline />
        <StringListField label="Environmental hazards" items={phys.environmental_hazards} isEditing={isEditing} onChange={onChange} path={[...PATH, 'physical_environment', 'environmental_hazards']} />
      </SubSection>

      <SubSection title="Usage scenarios" count={scenarios.length}>
        <ObjectListField columns={scenarioColumns} rows={scenarios} isEditing={isEditing} onChange={onChange} path={[...PATH, 'usage_scenarios', 'scenarios']} />
      </SubSection>

      <SubSection title="Network threat environment" count={threatEnvs.length}>
        <ObjectListField columns={threatColumns} rows={threatEnvs} isEditing={isEditing} onChange={onChange} path={[...PATH, 'network_threat_environment', 'assessments']} />
      </SubSection>
    </SectionCard>
  );
}
